import {
  Component,
  inject,
  OnDestroy,
  signal,
  computed,
  input,
  ChangeDetectionStrategy,
  output,
  viewChild,
  OnInit,
  linkedSignal,
} from '@angular/core';
import { CoinflowService } from '../../../../core/coinflow.js';
import {
  AlertService,
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  IconComponent,
  LoadingComponent,
} from '@gains-com/ui-angular';
import { CurrencyPipe } from '@angular/common';
import { PurchaseDialogHeaderComponent } from '../purchase-dialog-header/purchase-dialog-header.component.js';
import { NameMismatchNoticeComponent } from '../name-mismatch-notice/name-mismatch-notice.component.js';
import {
  Coinflow3DSCheckoutError,
  CoinflowCardPaymentForm,
  CoinflowCardMetadata,
  coinflowErrorMessage,
  create3DSPromptOptions,
  handleSessionTokenRefusal,
  isCoinflowNameVerificationDecline,
  isMissingSavedCardToken,
  isPurchaseLimitReachedError,
  PurchaseSelection,
} from '../../common.js';
import { coinflowCardFormArgs, tokenizeCardForm, tokenizeNewCard } from '../../coinflow-card-form.js';
import {
  CurrencyId,
  PaymentEntityTypeId,
  PaymentMethod,
  PaymentMethodEntityTypeId,
  PaymentProviderId,
} from '@gains-com/shared';
import { CoinflowCardForm } from '@coinflowlabs/angular';
import { AuthService } from '../../../../core/auth.service.js';
import { environment } from '../../../../../environments/environment.js';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { filter, Subscription, tap } from 'rxjs';
import { GooglePlacesService } from '../../../../core/google-places.js';
import { CoinflowCheckoutResponse } from '../../../../core/coinflow-checkout.service.js';
import { PurchaseService } from '../../../../core/purchase.service.js';
import { ChallengeModalComponent } from '../coinflow-challenge-modal/coinflow-challenge-modal.component.js';
import { PaymentMethodsService } from '../../../../core/payment-methods.js';
import { PurchaseReceiptComponent } from '../purchase-receipt/purchase-receipt.component.js';
import { BillingNameWarningComponent } from '../billing-name-warning/billing-name-warning.component.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { SelfApiService } from '../../../../core/self-api.service.js';
import { LocationGateViewService } from '../../../../core/view-states/location-gate-view.service.js';
import { UserAddress } from '../../../../core/address.models.js';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { Analytics } from '../../../../core/analytics/analytics.service.js';
import { ActivePurchaseViewService } from '../../../../core/view-states/active-purchase-view.service.js';
import { PaymentMethodsViewService } from '../../../../core/view-states/payment-methods-view.service.js';

interface CardPurchaseForm {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  address1: FormControl<string | null>;
  address2: FormControl<string | null>;
  city: FormControl<string | null>;
  state: FormControl<string | null>;
  zip: FormControl<string | null>;
  country: FormControl<string | null>;
}

interface Coinflow3DSState extends Coinflow3DSCheckoutError {
  cardForm?: CoinflowCardPaymentForm;
  savedPaymentMethod?: PaymentMethod<CoinflowCardMetadata> | null;
  last4?: string;
  expiry: string;
  challengeComplete: boolean;
  token: string;
}

@Component({
  selector: 'app-card-purchase',
  templateUrl: './card-purchase.component.html',
  styleUrls: ['../styles.scss', './card-purchase.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    CardContentComponent,
    PurchaseDialogHeaderComponent,
    CoinflowCardForm,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatCheckbox,
    CurrencyPipe,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    MatAutocompleteTrigger,
    ChallengeModalComponent,
    ButtonLoadingDirective,
    IconComponent,
    LoadingComponent,
    PurchaseReceiptComponent,
    BillingNameWarningComponent,
    NameMismatchNoticeComponent,
    MatTooltip,
  ],
})
export class CardPurchaseComponent implements OnDestroy, OnInit {
  // Service Injections
  private readonly _purchaseService = inject(PurchaseService);
  private readonly _googlePlaces = inject(GooglePlacesService);
  private readonly _auth = inject(AuthService);
  private readonly _alertService = inject(AlertService);
  private readonly _userView = inject(UserViewService);
  private readonly _paymentMethodsService = inject(PaymentMethodsService);
  private readonly _coinflow = inject(CoinflowService);
  private readonly _analytics = inject(Analytics);
  private readonly _activePurchaseViewService = inject(ActivePurchaseViewService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _selfApi = inject(SelfApiService);
  private readonly _locationGate = inject(LocationGateViewService);
  private readonly _logger = inject(LOGGER_FACTORY).create('CardPurchase');

  // Input/Output Properties
  purchaseSelection = input.required<PurchaseSelection>();
  preselectedSavedCard = input<PaymentMethod<CoinflowCardMetadata> | null>(null);

  // Coinflow session token — fetched on component init
  coinflowSessionToken = signal<string | null>(null);
  back = output<void>();
  close = output<'contact-support' | void>();
  paymentInfo = output<CoinflowCardPaymentForm>();
  purchaseLimitReached = output<void>();

  // ViewChild References
  newCardChild = viewChild<CoinflowCardForm>('newCardChild');
  savedCardChild = viewChild<CoinflowCardForm>('savedCardChild');

  // Observable to Signal Conversions
  private readonly _allPaymentMethods = toSignal(this._paymentMethodsViewService.visiblePaymentMethods$);
  private readonly _paymentMethodsLoading = toSignal(this._paymentMethodsViewService.loading$);

  user = toSignal(this._userView.state$);
  fullName = computed(() => `${this.user()?.first_name ?? ''} ${this.user()?.last_name ?? ''}`.trim());

  // Computed Signals
  amount = computed(() => this.purchaseSelection()?.inputAmount?.toNumber() ?? 0);
  paymentMethods = computed(
    () =>
      (this._allPaymentMethods()?.filter(
        (p) => p.visible_to_user && p.payment_method_entity_type_id === PaymentMethodEntityTypeId.CoinflowCardToken,
      ) ?? []) as PaymentMethod<CoinflowCardMetadata>[],
  );
  paymentMethodsLoading = computed(() => this._paymentMethodsLoading() ?? true);

  // The hosted form reads its args once at init, so the saved-card form is
  // keyed on the token in the template to remount on a card switch.
  newCardFormArgs = coinflowCardFormArgs({
    merchantId: environment.coinflow.merchantId,
    env: environment.coinflow.env,
    variant: 'card-form',
    accent: '#7332f5',
  });
  savedCardFormArgs = computed(() =>
    coinflowCardFormArgs({
      merchantId: environment.coinflow.merchantId,
      env: environment.coinflow.env,
      variant: 'cvv-form',
      accent: '#7332f5',
      token: this.savedPaymentMethod()?.payment_method_entity_id,
    }),
  );

  // Reactive Signals
  loading = signal(false);
  nameMismatchDeclined = signal(false);
  addressDone = signal(false);
  showFullAddress = signal(false);
  _3dsState = signal<Coinflow3DSState | null>(null);

  // Saved default address (GET /self/address, quiet background prefetch) —
  // null (missing or fetch failure) hides the Use Saved Address checkbox.
  savedAddress = signal<UserAddress | null>(null);
  useSavedAddress = signal(false);

  // Derived-and-writable initial state from the preselected saved card.
  // Replaces a state-propagation effect: when a saved card is preselected we
  // start in the saved-card / verify-CVV flow; otherwise (once payment methods
  // have loaded) we start on the new-card flow. These still accept manual
  // .set() from the template (verify-CVV back button) and the submit() error
  // paths, which persist because the preselected input never changes after the
  // component is created.
  savedPaymentMethod = linkedSignal<PaymentMethod<CoinflowCardMetadata> | null>(() => this.preselectedSavedCard());
  verifyCvv = linkedSignal(() => !!this.preselectedSavedCard());
  newCard = linkedSignal(() => !this.preselectedSavedCard() && !this._paymentMethodsLoading());

  // Form Controls
  cardPurchaseForm = new FormGroup<CardPurchaseForm>({
    firstName: new FormControl<string | null>(null),
    lastName: new FormControl<string | null>(null),
    address1: new FormControl<string | null>(null),
    address2: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
    state: new FormControl<string | null>(null),
    zip: new FormControl<string | null>(null),
    country: new FormControl<string | null>(null),
  });

  places = signal<
    {
      text: string;
      value: string;
    }[]
  >([]);

  private _address1Sub?: Subscription;
  private _googleAutocompleteDebounce: NodeJS.Timeout | null = null;

  async ngOnInit() {
    // Background prefetch of the saved default address — quiet variant so a
    // failure never toasts; the billing form just starts blank.
    void this._selfApi.getAddressQuiet().then((res) => {
      if (res?.address) {
        this.savedAddress.set(res.address);
        // A slow response must never clobber input the user already typed —
        // only auto-apply while the address fields are still untouched. The
        // checkbox still renders (unchecked) so opting in stays possible.
        if (this._addressFieldsBlank()) {
          this.useSavedAddress.set(true);
          this._applySavedAddress(true);
        }
      }
    });

    await this._paymentMethodsViewService.syncPaymentMethods();

    try {
      this.coinflowSessionToken.set(await this._coinflow.createSessionToken('purchase'));
    } catch (err: any) {
      if (isPurchaseLimitReachedError(err)) {
        this.purchaseLimitReached.emit();
        return;
      }
      if (handleSessionTokenRefusal(err, this._alertService)) {
        return;
      }

      this._logger.error('Failed to create Coinflow session token', {
        stack: err,
      });
    }
  }

  toggleUseSavedAddress(checked: boolean) {
    this.useSavedAddress.set(checked);
    this._applySavedAddress(checked);
  }

  private _addressFieldsBlank(): boolean {
    const { address1, address2, city, state, zip, country } = this.cardPurchaseForm.value;
    return !address1 && !address2 && !city && !state && !zip && !country;
  }

  // emitEvent: false ALWAYS — the address1 valueChanges subscription feeds the
  // Google Places autocomplete; a programmatic patch must not trigger it.
  private _applySavedAddress(apply: boolean) {
    const address = this.savedAddress();
    if (apply && address) {
      this.cardPurchaseForm.patchValue(
        {
          address1: address.address1,
          address2: address.address2 ?? '',
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: 'US',
        },
        { emitEvent: false },
      );
    } else {
      this.cardPurchaseForm.patchValue(
        { address1: null, address2: null, city: null, state: null, zip: null, country: null },
        { emitEvent: false },
      );
    }
  }

  constructor() {
    let firstSearch = true;
    this._address1Sub = this.cardPurchaseForm.controls.address1.valueChanges.subscribe((v) => {
      if (!v) {
        return;
      }

      if (this._googleAutocompleteDebounce) {
        clearTimeout(this._googleAutocompleteDebounce);
      }

      const query = () => {
        (async () => {
          try {
            const predictions = await this._googlePlaces.googlePlacesAutocomplete(v);

            this.places.set(
              predictions.suggestions.map((s) => ({
                text: s.placePrediction.text.text,
                value: s.placePrediction.placeId,
              })),
            );
          } catch (e: any) {
            this._alertService.notifyDanger(
              'Purchase Failed',
              'An unexpected error occurred while processing your purchase. Please try again later.',
            );

            this._logger.error(e);
          }
        })();
      };

      if (firstSearch) {
        query();
        firstSearch = false;
      } else {
        this._googleAutocompleteDebounce = setTimeout(() => {
          query();
        }, 250);
      }
    });

    // Patch the billing name from the user record whenever it arrives/changes.
    // Declarative side effect off the user stream — auto-cleaned on destroy.
    toObservable(this.user)
      .pipe(
        filter((u): u is NonNullable<typeof u> => !!u),
        tap((u) => {
          this.cardPurchaseForm.controls.firstName.setValue(u.first_name ?? '');
          this.cardPurchaseForm.controls.lastName.setValue(u.last_name ?? '');
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  purchaseQuery = computed(() => this._activePurchaseViewService.activePurchasePaymentEntity());

  ngOnDestroy() {
    this._address1Sub?.unsubscribe();
  }

  async selectAutocompleteAddress(placeId: string) {
    if (!this.showFullAddress()) {
      if (this._googleAutocompleteDebounce) {
        clearTimeout(this._googleAutocompleteDebounce);
      }

      if (!this._address1Sub?.closed) {
        this._address1Sub?.unsubscribe();
      }

      const placeDetails = await this._googlePlaces.googlePlacesGetPlaceDetail(placeId);
      const streetNumber = placeDetails.addressComponents.find((a) => a.types.includes('street_number'))?.longText;
      const route = placeDetails.addressComponents.find((a) => a.types.includes('route'))?.shortText;
      const city = placeDetails.addressComponents.find((a) => a.types.includes('locality'))?.longText;
      const state = placeDetails.addressComponents.find((a) =>
        a.types.includes('administrative_area_level_1'),
      )?.shortText;
      const zip = placeDetails.addressComponents.find((a) => a.types.includes('postal_code'))?.longText;
      const country = placeDetails.addressComponents.find((a) => a.types.includes('country'))?.shortText;

      if (streetNumber && route) {
        this.cardPurchaseForm.controls.address1.setValue(`${streetNumber} ${route}`);
      }

      if (city) {
        this.cardPurchaseForm.controls.city.setValue(city);
      }

      if (state) {
        this.cardPurchaseForm.controls.state.setValue(state);
      }

      if (zip) {
        this.cardPurchaseForm.controls.zip.setValue(zip);
      }

      if (country) {
        this.cardPurchaseForm.controls.country.setValue(country);
      }

      this._analytics.trackEvent('ftp_address_submitted', {
        value: this.amount(),
        currency: 'USD',
        provider: 'coinflow',
      });
    }
  }

  submitAddress() {
    this.cardPurchaseForm.markAllAsTouched();
    if (
      this.cardPurchaseForm.controls.address1.invalid ||
      this.cardPurchaseForm.controls.address2.invalid ||
      this.cardPurchaseForm.controls.zip.invalid ||
      this.cardPurchaseForm.controls.country.invalid ||
      this.cardPurchaseForm.controls.state.invalid ||
      this.cardPurchaseForm.controls.firstName.invalid ||
      this.cardPurchaseForm.controls.lastName.invalid
    ) {
      return;
    }

    this.addressDone.set(true);
  }

  private _createCardPaymentForm = async (): Promise<{ form: CoinflowCardPaymentForm; lastFour?: string }> => {
    const childComponent = this.newCardChild();
    if (!childComponent) {
      throw new Error('Card input component not found');
    }
    const { token: cardToken, expMonth, expYear, lastFour } = await tokenizeNewCard(childComponent);

    return {
      form: {
        cardToken,
        expYear,
        expMonth,
        email: this._auth.user!.email!,
        firstName: this.cardPurchaseForm.value.firstName!,
        lastName: this.cardPurchaseForm.value.lastName!,
        address1: this.cardPurchaseForm.value.address1!,
        address2: this.cardPurchaseForm.value.address2!,
        city: this.cardPurchaseForm.value.city!,
        state: this.cardPurchaseForm.value.state!,
        zip: this.cardPurchaseForm.value.zip!,
        country: this.cardPurchaseForm.value.country!,
      },
      lastFour,
    };
  };

  private _handleCoinflowError(
    err: any,
    amount: number,
    savedPaymentMethod: PaymentMethod<CoinflowCardMetadata> | null | undefined,
  ) {
    const errorMessage = coinflowErrorMessage(err);

    this.nameMismatchDeclined.set(isCoinflowNameVerificationDecline(err));
    this._alertService.notifyDanger('Purchase Failed', errorMessage);

    this._logger.error(`Card purchase purchase failed: ${errorMessage}`, {
      amount,
      saved_payment_method: savedPaymentMethod,
      error: err.error,
    });
  }

  async submit3DS() {
    this.loading.set(true);
    this.nameMismatchDeclined.set(false);

    // The charge POST goes straight to Coinflow, so this client-side re-check
    // (with the opt-in pre-submit silent re-attest) is the only location gate
    // at final submit. On block, the re-ask dialog resolves the continuation
    // and a grant falls through to the charge.
    if (!(await this._locationGate.requireVerifiedForAction('purchase', { preSubmitReattest: true }))) {
      this.loading.set(false);
      return;
    }

    const amount = this.amount()!;

    const { cardForm, transactionId, savedPaymentMethod, token: cardToken } = this._3dsState()!;

    const hasHadPurchase = this.user()?.has_had_purchase;

    try {
      let resp: CoinflowCheckoutResponse;

      if (!savedPaymentMethod) {
        resp = await this._purchaseService.coinflowCheckoutNewCard(
          amount,
          this.coinflowSessionToken(),
          {
            ...cardForm!,
            cardToken,
          },
          {
            transactionId,
          },
        );
      } else {
        resp = await this._purchaseService.coinflowCheckoutSavedCard(amount, this.coinflowSessionToken(), cardToken, {
          transactionId,
        });
      }

      const paymentId = resp.paymentId;

      if (paymentId) {
        this._activePurchaseViewService.setActivePurchasePaymentEntity({
          payment_entity_id: paymentId,
          payment_entity_type_id: PaymentEntityTypeId.CoinflowPayment,
          currency_id: CurrencyId.USD,
        });
      }

      if (!hasHadPurchase) {
        this._analytics.trackEvent('ftp_cc_submitted', {
          $insert_id: paymentId,
          value: amount,
          currency: 'USD',
          provider: 'coinflow',
        });
      }
    } catch (err: any) {
      // If the token is no longer delete the payment method so the user can try again
      if (isMissingSavedCardToken(err)) {
        this._alertService.notifyDanger(
          'Purchase Failed',
          'Error using saved card details. Re-enter the card details and attempt the purchase again.',
        );

        await this._paymentMethodsService.deletePaymentMethod({
          paymentMethodEntityId: savedPaymentMethod?.payment_method_entity_id ?? cardToken,
          paymentProviderId: [PaymentProviderId.Coinflow],
        });

        this.savedPaymentMethod.set(null);
      } else {
        this._handleCoinflowError(err, amount, savedPaymentMethod);
      }
    } finally {
      this.loading.set(false);
    }
  }

  async submit() {
    this.cardPurchaseForm.markAllAsTouched();
    const savedPaymentMethod = this.savedPaymentMethod();

    if (!savedPaymentMethod && this.cardPurchaseForm.invalid) {
      return;
    }

    const amount = this.amount()!;
    this.loading.set(true);
    this.nameMismatchDeclined.set(false);

    if (!(await this._locationGate.requireVerifiedForAction('purchase', { preSubmitReattest: true }))) {
      this.loading.set(false);
      return;
    }

    const hasHadPurchase = this.user()?.has_had_purchase;

    let token: string;

    try {
      let resp: CoinflowCheckoutResponse;
      let cardForm: { form: CoinflowCardPaymentForm; lastFour?: string } | undefined;

      if (!savedPaymentMethod) {
        cardForm = await this._createCardPaymentForm();

        token = cardForm.form.cardToken;

        resp = await this._purchaseService.coinflowCheckoutNewCard(
          amount,
          this.coinflowSessionToken(),
          cardForm.form,
          create3DSPromptOptions(),
        );
      } else {
        const savedCardChild = this.savedCardChild();
        if (!savedCardChild) {
          throw new Error('Saved card CVV component not found');
        }
        const cardToken = (await tokenizeCardForm(savedCardChild)).token;

        resp = await this._purchaseService.coinflowCheckoutSavedCard(
          amount,
          this.coinflowSessionToken(),
          cardToken,
          create3DSPromptOptions(),
        );
      }

      const coinflow3DSError = resp._3dsError;
      const paymentId = resp.paymentId;

      if (coinflow3DSError) {
        this._3dsState.set({
          ...coinflow3DSError,
          expiry: cardForm ? `${cardForm.form.expMonth}/${cardForm.form.expYear.slice(-2)}` : '',
          last4: cardForm?.lastFour ?? savedPaymentMethod?.metadata?.last4,
          cardForm: cardForm?.form,
          savedPaymentMethod,
          challengeComplete: false,
          token,
        });
      } else {
        if (!hasHadPurchase) {
          this._analytics.trackEvent('ftp_cc_submitted', {
            $insert_id: paymentId,
            value: amount,
            currency: 'USD',
            provider: 'coinflow',
          });
        }

        if (paymentId) {
          this._activePurchaseViewService.setActivePurchasePaymentEntity({
            payment_entity_id: paymentId,
            payment_entity_type_id: PaymentEntityTypeId.CoinflowPayment,
            currency_id: CurrencyId.USD,
          });
        }
      }
    } catch (err: any) {
      // If the token is no longer delete the payment method so the user can try again
      if (isMissingSavedCardToken(err)) {
        this._alertService.notifyDanger(
          'Purchase Failed',
          'Error using saved card details. Re-enter the card details and attempt the purchase again.',
        );

        await this._paymentMethodsService.deletePaymentMethod({
          paymentMethodEntityId: savedPaymentMethod?.payment_method_entity_id ?? token,
          paymentProviderId: [PaymentProviderId.Coinflow],
        });

        this.savedPaymentMethod.set(null);
        this.newCard.set(true);
      } else {
        this._handleCoinflowError(err, amount, savedPaymentMethod);
      }
    } finally {
      this.loading.set(false);
    }
  }

  async coinflowChallengeComplete() {
    this._3dsState.set({
      ...this._3dsState()!,
      challengeComplete: true,
    });

    await this.submit3DS();
  }
}
